import React, { useState, useEffect } from 'react';
import { IconX, IconDeviceFloppy, IconTrophy } from '@tabler/icons-react';
import api from '../api/api';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

const parsePar = (parData) => {
  if (!parData) return [];
  try { return JSON.parse(parData); } catch {
    return parData.split(',').map(v => Number(v.trim())).filter(v => !isNaN(v));
  }
};

const parseHoles = (jsonStr, key) => {
  if (!jsonStr) return {};
  try {
    const obj = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : jsonStr;
    return obj?.[key] || {};
  } catch { return {}; }
};

// 기본 설정
const DEFAULT_CONFIG = {
  handyGames: 5,
  handyMultiplier: 1.5,
  useHandyMultiplier: true,
  unitAmount: 1000,
  doubleType: 'current', // 'current' or 'next'
  birdie: true,
  yangpa: true,
  leading: true,
  participants: [],
  handicaps: {},
};

/**
 * 스크라치 설정 레이어
 */
export function ScratchSettingsLayer({ reservation, scores, members, existingConfig, onConfirm, onClose }) {
  const [config, setConfig] = useState(() => {
    if (existingConfig) return { ...DEFAULT_CONFIG, ...existingConfig };
    // 기본: 모든 참가자 선택
    const participants = scores.filter(s => s.joinYn !== 'N').map(s => s.playerSlot);
    return { ...DEFAULT_CONFIG, participants, handicaps: {} };
  });

  const [loadingHandy, setLoadingHandy] = useState(false);

  const activeScores = scores.filter(s => s.joinYn !== 'N');

  const getMemberName = (s) => {
    const mem = members.find(m => m.id === Number(s.userId));
    return mem?.nickname || s.playerName || `P${s.playerSlot}`;
  };

  // 핸디캡 자동계산 (레거시 방식: 유저별 "참가한" 최근N게임 평균 스코어 → 상대와의 차이 합산 × 가중치)
  const calcHandicaps = async () => {
    setLoadingHandy(true);
    const participantScores = activeScores.filter(s => config.participants.includes(s.playerSlot));

    // 1) 충분한 양의 과거 예약을 한번에 조회 (유저별 참가 게임이 다르므로 넉넉히)
    let allReservations = [];
    try {
      const res = await api.get('/golf/reservations', {
        params: {
          size: Math.max(config.handyGames * 5, 30), sortField: 'reservedDate', sortDir: 'desc',
          includeScores: true, dateTo: reservation?.reservedDate, status: 'COMPLETED', useYn: 'Y'
        }
      });
      allReservations = (res.data?.content || []).filter(r => r.id !== reservation?.id);
    } catch { /* ignore */ }

    // 2) 유저별로 "해당 유저가 참가한" 최근 (N+2)게임을 뽑고 min/max 제거 후 N게임 평균
    const beforeScore = {};
    for (const s of participantScores) {
      if (!s.userId) { beforeScore[s.playerSlot] = 72; continue; }
      const fetchCount = config.handyGames + 2; // min/max 제거용으로 +2
      const userScores = [];
      for (const r of allReservations) {
        if (userScores.length >= fetchCount) break;
        const rScores = r.scores || [];
        const userScore = rScores.find(rs => Number(rs.userId) === Number(s.userId) && rs.joinYn === 'Y');
        if (userScore && userScore.totalScore > 0) {
          userScores.push(userScore.totalScore);
        }
      }
      if (userScores.length >= 3) {
        // min/max 각 1개씩 제거
        userScores.sort((a, b) => a - b);
        const trimmed = userScores.slice(1, -1); // 최저1개, 최고1개 제거
        const avg = trimmed.reduce((a, b) => a + b, 0) / trimmed.length;
        beforeScore[s.playerSlot] = Math.round(avg);
      } else if (userScores.length > 0) {
        // 3게임 미만이면 그냥 평균
        beforeScore[s.playerSlot] = Math.round(userScores.reduce((a, b) => a + b, 0) / userScores.length);
      } else {
        beforeScore[s.playerSlot] = 72;
      }
    }

    // 3) 레거시 방식: me.handy = Σ(me.avgScore - you.avgScore) × alpha
    const handicaps = {};
    for (const me of participantScores) {
      let handy = 0;
      for (const you of participantScores) {
        if (me.playerSlot !== you.playerSlot) {
          handy += (beforeScore[me.playerSlot] - beforeScore[you.playerSlot]);
        }
      }
      if (config.useHandyMultiplier) handy = handy * config.handyMultiplier;
      handicaps[me.playerSlot] = handy;
    }
    setConfig(prev => ({ ...prev, handicaps, beforeScore }));
    setLoadingHandy(false);
  };

  useEffect(() => {
    if (config.participants.length > 0 && Object.keys(config.handicaps).length === 0) {
      calcHandicaps();
    }
    // eslint-disable-next-line
  }, []);

  const toggleParticipant = (slot) => {
    setConfig(prev => {
      const next = prev.participants.includes(slot)
        ? prev.participants.filter(s => s !== slot)
        : [...prev.participants, slot];
      return { ...prev, participants: next };
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, onClose)}>
      <div style={{ background: '#fff', color: '#333', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 420, maxHeight: '80vh', overflow: 'auto', padding: '16px 16px 20px' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <IconTrophy size={18} color="#f59e0b" />
            <span style={{ fontSize: 16, fontWeight: 700 }}>내기 자동 계산</span>
          </div>
          <button className="btn btn-sm btn-ghost-secondary" onClick={onClose}><IconX size={18} /></button>
        </div>

        {/* 핸디기준 */}
        <div className="mb-3">
          <label className="form-label mb-1" style={{ fontSize: 13, fontWeight: 600 }}>핸디기준</label>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: 12 }}>최근</span>
            <input type="number" value={config.handyGames} min={1} max={20}
              onChange={e => setConfig(p => ({ ...p, handyGames: Number(e.target.value) || 5 }))}
              style={{ width: 50, textAlign: 'center', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 12, padding: '2px 4px', background: '#fff', color: '#333' }} />
            <span style={{ fontSize: 12 }}>게임</span>
            <label className="form-check form-check-inline mb-0 ms-2" style={{ fontSize: 12 }}>
              <input type="checkbox" className="form-check-input" checked={config.useHandyMultiplier}
                onChange={e => setConfig(p => ({ ...p, useHandyMultiplier: e.target.checked }))} />
              핸디 가중치 :
            </label>
            <input type="number" value={config.handyMultiplier} step={0.1} min={0.5} max={3}
              onChange={e => setConfig(p => ({ ...p, handyMultiplier: Number(e.target.value) || 1.5 }))}
              style={{ width: 50, textAlign: 'center', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 12, padding: '2px 4px', background: '#fff', color: '#333' }} />
            <span style={{ fontSize: 12 }}>배 적용</span>
          </div>
        </div>

        {/* 내기참여 */}
        <div className="mb-3">
          <label className="form-label mb-1" style={{ fontSize: 13, fontWeight: 600 }}>내기참여</label>
          <div className="d-flex flex-wrap gap-2">
            {activeScores.map(s => (
              <label key={s.playerSlot} className="form-check form-check-inline mb-0" style={{ fontSize: 12 }}>
                <input type="checkbox" className="form-check-input"
                  checked={config.participants.includes(s.playerSlot)}
                  onChange={() => toggleParticipant(s.playerSlot)} />
                {getMemberName(s)}
              </label>
            ))}
          </div>
        </div>

        {/* 핸디 */}
        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <label className="form-label mb-0" style={{ fontSize: 13, fontWeight: 600 }}>핸디</label>
            <button className="btn btn-sm btn-outline-primary" style={{ fontSize: 11, padding: '1px 8px' }}
              onClick={calcHandicaps} disabled={loadingHandy}>
              {loadingHandy ? '계산중...' : '자동계산'}
            </button>
          </div>
          {activeScores.filter(s => config.participants.includes(s.playerSlot)).map(s => (
            <div key={s.playerSlot} className="d-flex align-items-center gap-2 mb-1">
              <span style={{ fontSize: 12, width: 80 }}>
                {getMemberName(s)} ({config.beforeScore?.[s.playerSlot] || s.totalScore || '-'})
              </span>
              <input type="number" value={config.handicaps[s.playerSlot] ?? 0} step="any"
                onChange={e => setConfig(p => ({
                  ...p, handicaps: { ...p.handicaps, [s.playerSlot]: Number(e.target.value) || 0 }
                }))}
                style={{ width: 60, textAlign: 'center', border: '1px solid #dee2e6', borderRadius: 4, fontSize: 12, padding: '2px 4px', background: '#fff', color: '#333' }} />
            </div>
          ))}
        </div>

        {/* 빵 */}
        <div className="mb-3">
          <label className="form-label mb-1" style={{ fontSize: 13, fontWeight: 600 }}>삥 (원)</label>
          <input type="number" value={config.unitAmount} min={100} step={100}
            onChange={e => setConfig(p => ({ ...p, unitAmount: Number(e.target.value) || 1000 }))}
            style={{ width: 100, border: '1px solid #dee2e6', borderRadius: 4, fontSize: 13, padding: '4px 8px', background: '#fff', color: '#333' }} />
        </div>

        {/* 배판 */}
        <div className="mb-3">
          <label className="form-label mb-1" style={{ fontSize: 13, fontWeight: 600 }}>배판</label>
          <div className="d-flex gap-3 mb-1">
            <label className="form-check mb-0" style={{ fontSize: 12 }}>
              <input type="radio" className="form-check-input" name="doubleType" value="next"
                checked={config.doubleType === 'next'} onChange={() => setConfig(p => ({ ...p, doubleType: 'next' }))} />
              다음판 배판
            </label>
            <label className="form-check mb-0" style={{ fontSize: 12 }}>
              <input type="radio" className="form-check-input" name="doubleType" value="current"
                checked={config.doubleType === 'current'} onChange={() => setConfig(p => ({ ...p, doubleType: 'current' }))} />
              이번판 배판
            </label>
          </div>
          <div className="d-flex gap-3">
            <label className="form-check form-check-inline mb-0" style={{ fontSize: 12 }}>
              <input type="checkbox" className="form-check-input" checked={config.birdie}
                onChange={e => setConfig(p => ({ ...p, birdie: e.target.checked }))} />
              버디
            </label>
            <label className="form-check form-check-inline mb-0" style={{ fontSize: 12 }}>
              <input type="checkbox" className="form-check-input" checked={config.yangpa}
                onChange={e => setConfig(p => ({ ...p, yangpa: e.target.checked }))} />
              양파
            </label>
            <label className="form-check form-check-inline mb-0" style={{ fontSize: 12 }}>
              <input type="checkbox" className="form-check-input" checked={config.leading}
                onChange={e => setConfig(p => ({ ...p, leading: e.target.checked }))} />
              선두등
            </label>
          </div>
        </div>

        {/* 버튼 */}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>취소</button>
          <button className="btn btn-sm btn-warning d-flex align-items-center gap-1"
            onClick={() => onConfirm(config)}>
            <IconDeviceFloppy size={14} /> 확인
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 스크라치 내기 계산
 */
export function calculateScratch(config, scores, sections) {
  const { participants, handicaps, unitAmount, birdie, yangpa, leading, doubleType } = config;
  const activeScores = scores.filter(s => participants.includes(s.playerSlot) && s.joinYn !== 'N');
  if (activeScores.length < 2) return null;

  // 모든 홀 데이터 수집
  const allHoles = [];
  sections.forEach((sec, secIdx) => {
    const pars = parsePar(sec.parData);
    const holes = Array.from({ length: sec.holesCount || 9 }, (_, i) => i + 1);
    holes.forEach((h, hi) => {
      allHoles.push({ secIdx, holeNum: h, par: pars[hi] || 4, holeIndex: allHoles.length });
    });
  });

  // 홀별 결과
  const results = [];
  const playerMoney = {};
  activeScores.forEach(s => { playerMoney[s.playerSlot] = 0; });

  let doubleMultiplier = 1;

  allHoles.forEach((hole) => {
    const { secIdx, holeNum, par } = hole;
    const holeScores = {};
    const holeOverPars = {};

    activeScores.forEach(s => {
      const hd = parseHoles(s.holeScoresJson, `no${secIdx + 1}`);
      const v = hd[String(holeNum)];
      const overPar = (v != null && v !== '') ? Number(v) : null;
      holeScores[s.playerSlot] = overPar;
      holeOverPars[s.playerSlot] = overPar;
    });

    const validScores = Object.entries(holeOverPars).filter(([, v]) => v != null);
    if (validScores.length < 2) {
      results.push({ ...hole, money: { ...Object.fromEntries(activeScores.map(s => [s.playerSlot, 0])) }, remarks: '' });
      return;
    }

    const minScore = Math.min(...validScores.map(([, v]) => v));
    const winners = validScores.filter(([, v]) => v === minScore).map(([k]) => Number(k));

    // 배판 조건 판정 (우선순위: 버디/이글 → 양파 → 선두동)
    let isDouble = false;
    let doubleName = '';

    // 버디/이글: 누구든 -1 이하
    if (birdie && validScores.some(([, v]) => v <= -2)) {
      doubleName = doubleType === 'next' ? '전판 이글' : '이글';
      isDouble = true;
    }
    if (birdie && validScores.some(([, v]) => v === -1)) {
      doubleName = doubleType === 'next' ? '전판 버디' : '버디';
      isDouble = true;
    }
    // 양파: 누구든 더블파 이상 (overPar >= par)
    if (yangpa && !isDouble) {
      if (validScores.some(([, v]) => v >= par)) {
        doubleName = doubleType === 'next' ? '전판 양파' : '양파';
        isDouble = true;
      }
    }
    // 선두동: 선두가 2인 이상
    if (leading && !isDouble) {
      if (winners.length >= 2) {
        doubleName = doubleType === 'next' ? '전판 선두동' : '선두동';
        isDouble = true;
      }
    }

    // 이번판 배판: 이 홀에 바로 적용
    let won;
    if (doubleType === 'current') {
      won = isDouble ? unitAmount * 2 : unitAmount;
    } else {
      // 다음판 배판: 이전 홀에서 설정된 doubleMultiplier 적용
      won = unitAmount * doubleMultiplier;
    }

    // 다음판 배판: 이 홀의 조건이 다음 홀에 영향
    if (doubleType === 'next') {
      doubleMultiplier = isDouble ? 2 : 1;
    }

    // 머니 계산 (레거시 방식: 모든 쌍 간 타수 차이 × 삥 + 버디/이글 보너스)
    const holeMoney = {};
    activeScores.forEach(s => { holeMoney[s.playerSlot] = 0; });

    validScores.forEach(([mySlot, myScore]) => {
      validScores.forEach(([yourSlot, yourScore]) => {
        if (mySlot === yourSlot) return;
        let money = 0;
        if (myScore < yourScore) {
          money = (yourScore - myScore) * won;
          if (myScore <= -2) money += won * 2;
          else if (myScore === -1) money += won;
        } else if (myScore > yourScore) {
          money = (yourScore - myScore) * won;
          if (yourScore <= -2) money -= won * 2;
          else if (yourScore === -1) money -= won;
        }
        holeMoney[Number(mySlot)] += money;
      });
    });

    // 누적
    Object.entries(holeMoney).forEach(([k, v]) => { playerMoney[Number(k)] += v; });

    results.push({ ...hole, money: holeMoney, remarks: doubleName });
  });

  // 합계
  const totals = { ...playerMoney };

  // 핸디 정산 (레거시 방식: 핸디값 × 삥, 양수면 올림, 음수면 내림)
  const handyAdjust = {};
  activeScores.forEach(s => {
    const h = handicaps[s.playerSlot] || 0;
    const rounded = h > 0 ? Math.ceil(h) : Math.floor(h);
    handyAdjust[s.playerSlot] = rounded * unitAmount;
  });
  const finalMoney = {};
  activeScores.forEach(s => {
    finalMoney[s.playerSlot] = totals[s.playerSlot] + handyAdjust[s.playerSlot];
  });

  return {
    results,
    totals,
    handicaps: handyAdjust,
    finalMoney,
    activeScores,
    allHoles,
  };
}

/**
 * 스크라치 결과 테이블
 */
export function ScratchResultTable({ scratchData, members, scores }) {
  if (!scratchData) return null;
  const { results, totals, handicaps, finalMoney, activeScores } = scratchData;

  const getMemberName = (s) => {
    const mem = members.find(m => m.id === Number(s.userId));
    return mem?.nickname || s.playerName || `P${s.playerSlot}`;
  };

  const moneyColor = (v) => v > 0 ? '#2fb344' : v < 0 ? '#e03131' : undefined;
  const fmtMoney = (v) => {
    if (!v) return '';
    const abs = Math.abs(v);
    const sign = v > 0 ? '+' : '-';
    const main = Math.floor(abs / 1000);
    const rest = abs % 1000;
    if (main === 0) return <>{sign}{abs}</>;
    return <>{sign}{main.toLocaleString()}<span style={{ fontSize: '0.7em', opacity: 0.5 }}>,{String(rest).padStart(3, '0')}</span></>;
  };

  // ScoreCell (스코어카드와 동일 스타일)
  const ScratchScoreCell = ({ value, par }) => {
    if (value === '' || value == null) return <span style={{ color: '#ccc' }}>-</span>;
    const v = Number(value);
    if (isNaN(v)) return <span style={{ color: '#ccc' }}>-</span>;
    const isDoublePar = par > 0 && v === par;
    const base = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, fontSize: 10, lineHeight: 1 };
    if (v <= -2) return <span style={{ ...base, border: 'double 3px #dd4b39', borderRadius: '50%', fontWeight: 'bold', color: '#dd4b39' }}>{v}</span>;
    if (v === -1) return <span style={{ ...base, border: 'solid 1px #dd4b39', borderRadius: '50%', color: '#dd4b39' }}>{v}</span>;
    if (v === 0) return <span style={{ ...base }}>{v}</span>;
    if (isDoublePar) return <span style={{ ...base, background: '#555', color: '#fff' }}>{v}</span>;
    if (v === 1) return <span style={{ ...base, border: 'solid 1px #337ab7', color: '#337ab7' }}>{v}</span>;
    if (v === 2) return <span style={{ ...base, border: 'double 3px #337ab7', color: '#337ab7' }}>{v}</span>;
    return <span style={{ ...base, background: '#ccc' }}>{v}</span>;
  };

  return (
    <div className="border-top mt-2">
      <div className="px-2 py-2 d-flex align-items-center gap-2" style={{ background: '#fffbe6' }}>
        <IconTrophy size={16} color="#f59e0b" />
        <span style={{ fontSize: 14, fontWeight: 700 }}>스크라치 결과</span>
        <span style={{ fontSize: 10, color: '#888' }}>(단위: 원)</span>
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-0 text-center" style={{ fontSize: 11, tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              <th style={{ width: 20, padding: '2px 0' }}></th>
              <th style={{ width: 16, padding: '2px 0' }}></th>
              <th colSpan={activeScores.length} style={{ padding: '2px 0', fontSize: 10, background: '#e8f4fd' }}>💰 MONEY</th>
              <th colSpan={activeScores.length} style={{ padding: '2px 0', fontSize: 10, background: '#e8f8e8' }}>⛳ SCORE</th>
              <th style={{ width: 28, padding: '2px 0' }}></th>
            </tr>
            <tr style={{ background: '#f0f4f8', fontSize: 9 }}>
              <th style={{ padding: '1px 0' }}>H</th>
              <th style={{ padding: '1px 0' }}>P</th>
              {activeScores.map(s => (
                <th key={`m${s.playerSlot}`} style={{ padding: '1px 0' }}>{getMemberName(s)}</th>
              ))}
              {activeScores.map(s => (
                <th key={`s${s.playerSlot}`} style={{ padding: '1px 0' }}>{getMemberName(s)}</th>
              ))}
              <th style={{ padding: '1px 0', fontSize: 8 }}>비고</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => (
              <tr key={i}>
                <td style={{ padding: '1px 0', fontSize: 10 }}>{r.holeIndex + 1}</td>
                <td style={{ padding: '1px 0', fontSize: 10 }}>{r.par}</td>
                {activeScores.map(s => {
                  const money = r.money[s.playerSlot] || 0;
                  return (
                    <td key={`m${s.playerSlot}`} style={{ padding: '1px 0', color: moneyColor(money), fontWeight: money !== 0 ? 600 : undefined, fontSize: 10 }}>
                      {money !== 0 ? fmtMoney(money) : ''}
                    </td>
                  );
                })}
                {activeScores.map(s => {
                  const hd = parseHoles(s.holeScoresJson, `no${r.secIdx + 1}`);
                  const score = hd[String(r.holeNum)];
                  return (
                    <td key={`s${s.playerSlot}`} style={{ padding: '1px 0', verticalAlign: 'middle' }}>
                      <ScratchScoreCell value={score} par={r.par} />
                    </td>
                  );
                })}
                <td style={{ padding: '1px 0', fontSize: 8, color: '#888' }}>{r.remarks}</td>
              </tr>
            ))}
            {/* 합계 */}
            <tr style={{ background: '#f8f9fa', fontWeight: 700 }}>
              <td colSpan={2} style={{ padding: '2px 0', fontSize: 10 }}>합계</td>
              {activeScores.map(s => (
                <td key={`m${s.playerSlot}`} style={{ padding: '2px 0', color: moneyColor(totals[s.playerSlot]), fontSize: 10 }}>
                  {fmtMoney(totals[s.playerSlot])}
                </td>
              ))}
              {activeScores.map(s => (
                <td key={`s${s.playerSlot}`} style={{ padding: '2px 0', fontSize: 10 }}>{s.totalScore || ''}</td>
              ))}
              <td></td>
            </tr>
            {/* 핸디 */}
            <tr style={{ background: '#f0f4f8' }}>
              <td colSpan={2} style={{ padding: '2px 0', fontSize: 10 }}>핸디</td>
              {activeScores.map(s => (
                <td key={`m${s.playerSlot}`} style={{ padding: '2px 0', color: moneyColor(handicaps[s.playerSlot]), fontSize: 10 }}>
                  {fmtMoney(handicaps[s.playerSlot])}
                </td>
              ))}
              {activeScores.map(s => <td key={`s${s.playerSlot}`}></td>)}
              <td></td>
            </tr>
            {/* 최종금액 */}
            <tr style={{ background: '#fffbe6', fontWeight: 800 }}>
              <td colSpan={2} style={{ padding: '3px 0', fontSize: 10 }}>최종</td>
              {activeScores.map(s => (
                <td key={`m${s.playerSlot}`} style={{ padding: '3px 0', fontSize: 12, color: moneyColor(finalMoney[s.playerSlot]) }}>
                  {fmtMoney(finalMoney[s.playerSlot])}
                </td>
              ))}
              <td colSpan={activeScores.length + 1} style={{ fontSize: 9, color: '#206bc4', textAlign: 'left', padding: '3px 4px', fontWeight: 400 }}>
                ← 금액 단위 원
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
